/**
 * The editor's own parts: the schema a block of runs is held in, and the plugins
 * that draw what is projected onto it.
 *
 * Kept apart from the component so the component holds what a reader can see and
 * this holds how ProseMirror is told to hold it. Nothing here names a colour or
 * a class: the classes are handed in, because what a reference looks like is the
 * design's answer and not the schema's.
 */
import {
  Extension,
  Mark,
  Node as TipTapNode,
  type JSONContent,
} from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { joinBackward, splitBlock } from "@tiptap/pm/commands";
import { NodeSelection } from "@tiptap/pm/state";
import type { EditorState } from "@tiptap/pm/state";
import type { Node as ProseNode } from "@tiptap/pm/model";
import type { Editor } from "@tiptap/react";
import {
  STATED,
  asBlocks,
  offerLeft,
  phrasesBefore,
  type BlockControl,
  type Offer,
  type ReferenceSource,
  type TextBlock,
} from "./structured-text";

/** How wide the list is drawn, the same as the design's menu panel. */
const OFFER_WIDTH = 290;

/**
 * The words that name something, held as one thing rather than as marked prose.
 *
 * An atom, so the caret can never sit between the noun and what it shows: the
 * writer moving past it is past all of it, and a backspace selects the whole
 * reference before a second one removes it. Held as a mark first, and the
 * numeral drawn beside it took up room the caret could not reach, so the writer
 * could not tell where the next words would land.
 *
 * `text` is the writer's own words. What the reference shows is not held here at
 * all: the node view asks the source for it on every draw, so a renumbering
 * changes every claim and rewrites none of them.
 */
export function Reference(wordsClass: string) {
  return TipTapNode.create({
    name: "reference",
    group: "inline",
    inline: true,
    atom: true,
    selectable: true,
    addAttributes() {
      // Read back from the shape renderHTML writes, or a copied reference comes
      // back naming nothing and holding no words: the clipboard round-trips
      // through that HTML, and the default parser looks for attributes of the
      // same name, which the rendered span does not carry.
      return {
        reference: {
          default: null,
          parseHTML: (element) => element.getAttribute("data-reference"),
          renderHTML: (attributes) => ({
            "data-reference": String(attributes.reference),
          }),
        },
        text: {
          default: "",
          parseHTML: (element) => element.textContent,
          renderHTML: () => ({}),
        },
      };
    },
    parseHTML() {
      return [{ tag: "span[data-reference]" }];
    },
    renderHTML({ node }: { node: ProseNode }) {
      return [
        "span",
        {
          "data-reference": String(node.attrs.reference),
          class: wordsClass,
        },
        String(node.attrs.text),
      ];
    },
    addKeyboardShortcuts() {
      return {
        /**
         * The first press selects the whole reference, the second removes it.
         *
         * A reference is one thing, so backspacing into it should not eat a
         * letter of a name the writer chose whole. Selecting it first also shows
         * what the next press will take.
         */
        Backspace: () => {
          const { state, view } = this.editor;
          const { selection } = state;
          if (
            selection instanceof NodeSelection &&
            selection.node.type.name === this.name
          ) {
            view.dispatch(state.tr.deleteSelection());
            return true;
          }
          if (!selection.empty) return false;
          const before = selection.$from.nodeBefore;
          if (before?.type.name !== this.name) return false;
          const at = selection.from - before.nodeSize;
          view.dispatch(
            state.tr.setSelection(NodeSelection.create(state.doc, at)),
          );
          return true;
        },
      };
    },
  });
}

/**
 * What a stretch of words states, carried on the words themselves.
 *
 * A mark, so it moves with the words as the writer edits around them: words left
 * alone keep it, words typed inside it join it, and words removed take it with
 * them. Not inclusive, so words typed at its end are the writer's own and state
 * nothing. Drawn as nothing but the attribute, which the clipboard reads back.
 */
export function Stated() {
  return Mark.create({
    name: STATED,
    inclusive: false,
    addAttributes() {
      return {
        stated: {
          default: null,
          parseHTML: (element) => element.getAttribute("data-stated"),
          renderHTML: (attributes) => ({
            "data-stated": String(attributes.stated),
          }),
        },
      };
    },
    parseHTML() {
      return [{ tag: "span[data-stated]" }];
    },
    renderHTML({ HTMLAttributes }) {
      return ["span", HTMLAttributes, 0];
    },
  });
}

/**
 * What each reference shows, written onto it for the design to draw with
 * `::after`.
 *
 * Not held in the node and not drawn by a node view. A node view is rebuilt
 * only when its node changes, so a numeral that moved while the claim sat open
 * would keep the value it was first drawn with, and the whole point of
 * projecting it is that it cannot go stale.
 */
export function ReferenceTags(source: () => ReferenceSource | undefined) {
  return blockDecorations("referenceTags", (state) => {
    const references = source();
    if (references === undefined) return [];
    const drawn: Decoration[] = [];
    state.doc.descendants((node, position) => {
      if (node.type.name !== "reference") return;
      const named: unknown = node.attrs.reference;
      if (typeof named !== "string") return;
      const tag = references.tagOf(named);
      if (tag === null) return;
      const mark = references.markOf?.(named) ?? null;
      const open = mark?.open;
      drawn.push(
        Decoration.node(position, position + node.nodeSize, {
          // A tag that opens something is drawn as its own control after the
          // words, so the words stay the writer's to click into.
          ...(open === undefined ? { "data-reference-tag": tag } : {}),
          ...(mark === null ? {} : { title: mark.title }),
          ...(mark?.unconfirmed
            ? { "data-reference-unconfirmed": "true" }
            : {}),
        }),
      );
      if (open !== undefined && mark !== null) {
        drawn.push(
          Decoration.widget(
            position + node.nodeSize,
            () => tagControl(tag, mark.title, open),
            { side: -1, key: `${named}:${tag}`, ignoreSelection: true },
          ),
        );
      }
    });
    return drawn;
  });
}

/**
 * A tag that opens something: a button the editor neither writes into nor
 * selects, named by what it says on hover. A press is held from the editor, so
 * the caret stays where it was and the words are not touched.
 */
function tagControl(tag: string, title: string, open: () => void): HTMLElement {
  const control = document.createElement("button");
  control.type = "button";
  control.contentEditable = "false";
  control.textContent = tag;
  control.title = title;
  control.setAttribute("aria-label", title);
  control.dataset.referenceOpens = "true";
  control.addEventListener("mousedown", (event) => {
    event.preventDefault();
  });
  control.addEventListener("click", (event) => {
    event.preventDefault();
    open();
  });
  return control;
}

/**
 * Says that what a decoration is projected from has moved, so the set is built
 * again although the words have not changed.
 *
 * A numeral belongs to the drawings and a marker to the rule, and either can
 * change while the claim sits open. Without this the editor would keep drawing
 * what it first drew, and a projected thing that goes stale is worse than a
 * stored one, because nothing says it is stale.
 */
export const REDRAW = "structuredText:redraw";

/**
 * A set of block decorations, rebuilt when the words change or when what they
 * are projected from says so.
 *
 * ProseMirror asks for decorations on every state change, a moved caret
 * included. Answering with a fresh set each time redraws every block, and a
 * redraw during a click throws the selection away: the caret was read as the
 * start of the document, so every act that asks which line it is in got the
 * first one. Held in plugin state and mapped through a change instead.
 */
function blockDecorations(
  name: string,
  decorate: (state: EditorState) => Decoration[],
) {
  const key = new PluginKey<DecorationSet>(name);
  return Extension.create({
    name,
    addProseMirrorPlugins() {
      return [
        new Plugin<DecorationSet>({
          key,
          state: {
            init: (_config, state) =>
              DecorationSet.create(state.doc, decorate(state)),
            apply: (tr, set, _old, state) =>
              tr.docChanged || tr.getMeta(REDRAW) === true
                ? DecorationSet.create(state.doc, decorate(state))
                : set.map(tr.mapping, tr.doc),
          },
          props: {
            decorations: (state) => key.getState(state),
          },
        }),
      ];
    },
  });
}

/**
 * What each block is marked with, drawn before its words and never among them.
 *
 * Written onto the block as an attribute the design draws with `::before`, not
 * as a widget in the words. A pseudo-element cannot hold a caret or be
 * selected, so a marker never changes where the writer is.
 *
 * Projected like every other shown thing: a marker counts the blocks before it,
 * so numbering one letters every block after it without touching a word.
 */
export function BlockMarkers(
  markersOf: () =>
    ((blocks: readonly TextBlock[]) => readonly (string | null)[]) | undefined,
) {
  return blockDecorations("blockMarkers", (state) => {
    const project = markersOf();
    if (project === undefined) return [];
    const markers = project(asBlocks(state.doc.toJSON() as JSONContent));
    const drawn: Decoration[] = [];
    let index = 0;
    state.doc.forEach((node, offset) => {
      const marker = markers.at(index) ?? null;
      index += 1;
      if (marker === null) return;
      drawn.push(
        Decoration.node(offset, offset + node.nodeSize, {
          "data-block-marker": marker,
        }),
      );
    });
    return drawn;
  });
}

/** How each block is set, which is the consumer's business: a claim indents by depth. */
export function BlockClasses(classOf: (block: TextBlock) => string) {
  return blockDecorations("blockClasses", (state) => {
    const drawn: Decoration[] = [];
    state.doc.forEach((node, offset) => {
      const block = blockOf(node);
      const className = classOf(block);
      if (className !== "")
        drawn.push(
          Decoration.node(offset, offset + node.nodeSize, { class: className }),
        );
    });
    return drawn;
  });
}

/**
 * Backspace at the start of a block clears what the block is before it joins it
 * to the one above: the numbering first, then the indent, as a word processor
 * does. Only then does a further press join the words.
 *
 * Without it the first press pulls a limitation up into the one before it, which
 * is rarely what was meant and costs the attorney an undo to find out.
 */
export function ClearsBeforeJoining() {
  return Extension.create({
    name: "clearsBeforeJoining",
    addKeyboardShortcuts() {
      return {
        Backspace: () => {
          const { state, view } = this.editor;
          const { $from, empty } = state.selection;
          if (!empty || $from.parentOffset !== 0) return false;
          const block = $from.parent;
          const numbered: unknown = block.attrs.numbered;
          const depth: unknown = block.attrs.depth;
          const attrs =
            numbered === true
              ? { ...block.attrs, numbered: false }
              : typeof depth === "number" && depth > 0
                ? { ...block.attrs, depth: depth - 1 }
                : null;
          if (attrs !== null) {
            view.dispatch(
              state.tr.setNodeMarkup($from.before(), undefined, attrs),
            );
            return true;
          }
          // Nothing left to clear, so the words join the line above. Said here
          // rather than left to the default, which this editor's schema does
          // not bring: without it a plain line at the start simply sat there.
          return joinBackward(state, view.dispatch, view);
        },
      };
    },
  });
}

/**
 * No change may carry the words past as many blocks as the consumer keeps.
 *
 * Refused on the change itself rather than on the Enter key. Enter is the way a
 * writer adds one block, but it is not the way most claims would pass a cap:
 * pasting a page of prose builds a block for every line of it and never asks a
 * key binding anything. Held here, every route in is held to the same count.
 *
 * Said in the editor rather than when the words are read back, because the
 * editor would otherwise draw lines that nothing stores, and the writer would
 * find them gone on the next reload with nothing having said so.
 */
export function Capacity(blocks: number, refused: () => void) {
  return Extension.create({
    name: "capacity",
    addProseMirrorPlugins() {
      return [
        new Plugin({
          filterTransaction: (tr) => {
            if (!tr.docChanged || tr.doc.childCount <= blocks) return true;
            refused();
            return false;
          },
        }),
      ];
    },
  });
}

/**
 * Each act on the bar under the keys it says it answers to.
 *
 * The bar is drawn only once the writer is already in the words, and the words
 * spend Tab on the indent, so nothing moves focus to the bar: without this, an
 * act with no key of its own can be reached by the mouse and by nothing else.
 * The shortcut a control shows and the shortcut it answers to are then the same
 * fact, rather than a label beside a binding somewhere else.
 */
export function ControlKeys(
  controls: () => readonly BlockControl[],
  set: (editor: Editor, control: BlockControl) => void,
) {
  return Extension.create({
    name: "controlKeys",
    addKeyboardShortcuts() {
      const bound: Record<string, () => boolean> = {};
      for (const control of controls()) {
        if (control.shortcut === undefined) continue;
        bound[control.shortcut.replaceAll("+", "-")] = () => {
          const block = blockAt(this.editor);
          if (block === null || !(control.enabled?.(block) ?? true))
            return false;
          set(this.editor, control);
          return true;
        };
      }
      return bound;
    },
  });
}

/** Enter starts a block, Tab and Shift+Tab move one, no deeper than the feature allows. */
export function Nesting(deepest: number, under?: (block: TextBlock) => number) {
  return Extension.create({
    name: "nesting",
    addKeyboardShortcuts() {
      /**
       * The block Enter starts sits where the consumer says, not where the one
       * it follows sits. Carrying the depth over would make the line after a
       * claim's preamble a second preamble, which is not a thing a claim has.
       */
      const start = () => {
        if (under === undefined) return false;
        const { state, view } = this.editor;
        // The same guard move() carries: a selection over the whole document
        // resolves at the doc itself, which is no block and has no position
        // before it, so reading a depth off it answers about nothing.
        if (!state.selection.$from.parent.isTextblock) return false;
        const was = blockOf(state.selection.$from.parent);
        const depth = Math.min(deepest, Math.max(0, under(was)));
        const before = view.state.doc;
        if (!splitBlock(state, view.dispatch, view)) return false;
        // A split can be dispatched and still not happen: a cap on the blocks
        // refuses the change, and the words are left as they were. Setting the
        // depth then would set it on the block the writer is still in, so a
        // refused Enter on a full claim would quietly indent its preamble.
        if (view.state.doc === before) return true;
        const { $from } = view.state.selection;
        view.dispatch(
          view.state.tr.setNodeMarkup($from.before(), undefined, {
            ...$from.parent.attrs,
            depth,
            numbered: was.numbered,
          }),
        );
        return true;
      };
      const move = (by: number) => () => {
        const { state, view } = this.editor;
        const { $from } = state.selection;
        const block = $from.parent;
        // A selection over the whole document resolves at the doc itself, which
        // has no position before it and no depth to move. Select all and press
        // Tab and the throw escaped the key handler, indenting nothing.
        if (!block.isTextblock) return false;
        const depth: unknown = block.attrs.depth;
        const was = typeof depth === "number" ? depth : 0;
        const next = Math.min(deepest, Math.max(0, was + by));
        if (next === was) return true;
        view.dispatch(
          state.tr.setNodeMarkup($from.before(), undefined, {
            ...block.attrs,
            depth: next,
          }),
        );
        return true;
      };
      return { Enter: start, Tab: move(1), "Shift-Tab": move(-1) };
    },
  });
}

/**
 * One ProseMirror node read as the block a consumer sees.
 *
 * Through a document of one paragraph, because `asBlocks` reads a document and a
 * node on its own is not one. Written once: the cast and the wrapper are the same
 * three places over, and a change to how a node becomes a block is one edit here.
 */
function blockOf(node: ProseNode): TextBlock {
  const [block] = asBlocks({
    type: "doc",
    content: [node.toJSON() as JSONContent],
  });
  return block;
}

/** The block the caret sits in, as the consumer reads a block. */
export function blockAt(editor: Editor): TextBlock | null {
  const { $from } = editor.state.selection;
  const node = $from.parent;
  return node.isTextblock ? blockOf(node) : null;
}

/** What the words before the caret could name, or null while they could name nothing. */
export function offerAt(
  editor: Editor,
  source: ReferenceSource,
  /** What the list is positioned inside, which is not always the field itself. */
  within: HTMLElement | null,
): Offer | null {
  const { state } = editor;
  const { $from, empty } = state.selection;
  if (!empty) return null;
  // The words of the one text node the caret sits in, never the block's whole
  // text: a reference is one position in the document and no characters of its
  // text, so every offset taken across one lands a character early. It also
  // stops a phrase reading through a reference, and words either side of one
  // are not a name.
  const node = $from.nodeBefore;
  if (node === null || !node.isText) return null;
  const written = node.text ?? "";
  const nodeStart = $from.pos - written.length;
  for (const phrase of phrasesBefore(written, written.length)) {
    const choices = source.suggest(phrase.text);
    if (choices.length === 0) continue;
    const from = nodeStart + phrase.from;
    const caret = editor.view.coordsAtPos(from);
    // The box the list is drawn inside, not the field: with a bar above the
    // words the two differ by its height, and the list opened over the line
    // being typed.
    const box = (within ?? editor.view.dom).getBoundingClientRect();
    return {
      from,
      to: $from.pos,
      text: phrase.text,
      choices,
      // Against the editor's own box, so the list stays with the words while
      // the page scrolls under it, and never past the right edge of what holds
      // it: under a narrow rail the list is wider than the column, and opening
      // it at the caret would run it off the side of the screen.
      at: {
        left: offerLeft(caret.left, box.left, box.width, OFFER_WIDTH),
        top: caret.bottom - box.top,
      },
    };
  }
  return null;
}
